import { RESULT_STATUS, getErrorResult } from "./result";
import { isModuleRegistered } from "SharedStores/util";
import { PROGRAM_TYPES, PROGRAM_NAMESPACE } from "SharedStores/Program/programStore";
import { PROJECT_TYPES, PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";
import { HelperEvaluator } from "./helper";
import { isAttachmentInStatus, ATTACHMENT_APPROVAL_STATUS } from "Util/clientAttachment";
import { ENVELOPE_STATUS, isEnvelopeInStatus } from "ESignatures/eSignatureConstants";
import { isFiniteNumber } from "Util/util";

export const BOOLEAN_VALUE = {
    false: 0,
    true: 1
};

export class BooleanEvaluator extends HelperEvaluator {
    constructor(formulaEvaluator, storeContext) {
        super(formulaEvaluator, storeContext);
    }
    async and(exp) {
        const left = await this.evalBool(exp.Children[0]);
        const right = await this.evalBool(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }
        const value = left.Value && right.Value;

        return { Value: value };
    }

    async or(exp) {
        const left = await this.evalBool(exp.Children[0]);
        const right = await this.evalBool(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }
        const value = left.Value || right.Value;

        return { Value: value };
    }

    async not(exp) {
        const result = await this.evalBool(exp.Children[0]);
        return this._negateResult(result);
    }

    evaluateBooleanConstant(exp) {
        return { Value: Boolean(exp.ConstantValue && exp.ConstantValue !== BOOLEAN_VALUE.false) };
    }

    evaluateBooleanDataReference(exp) {
        exp.usingDefault = false;
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }
        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            const valStr = this.getters[PROJECT_TYPES.getters.dataFieldValueFromId](exp.PublicDataFieldDefinitionId);
            const val = valStr ? Boolean(valStr.toLowerCase() === "true") : null;
            if (val !== null) {
                return { Value: val };
            } else if (isFiniteNumber(exp.ConstantValue)) {
                exp.usingDefault = true;
                return { Value: exp.ConstantValue !== BOOLEAN_VALUE.false };
            }
            exp.inError = true;
            const dfd = this.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](exp.PublicDataFieldDefinitionId);
            exp.errorMessage = `No value for datafield ${dfd.Name}.`;
            return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
        }

        exp.inError = true;
        exp.errorMessage = "Project context not found to evaluate data field reference.";
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }

    evaluateAttachmentStatus(exp, attachmentStatusType) {
        exp.usingDefault = false;
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }

        let status = "";
        switch (attachmentStatusType) {
            case ATTACHMENT_APPROVAL_STATUS.approved:
                status = "Is Approved";
                break;
            case ATTACHMENT_APPROVAL_STATUS.rejected:
                status = "Is Rejected";
                break;
            default:
                throw Error(`Invalid attachment status type (${attachmentStatusType}).`);
        }

        const attDef = this.getters[PROGRAM_TYPES.getters.attachmentDefinitionFromId](exp.PublicAttachmentDefinitionId);

        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            const attachment = this.getters[PROJECT_TYPES.getters.attachmentFromId](exp.PublicAttachmentDefinitionId);

            if (attachment !== null) {
                return { Value: isAttachmentInStatus(attachment, attachmentStatusType) };
            } else if (isFiniteNumber(exp.ConstantValue)) {
                exp.usingDefault = true;
                return { Value: exp.ConstantValue !== BOOLEAN_VALUE.false };
            } else {
                exp.inError = true;
                exp.errorMessage = `There is no attachment for '${attDef.Name}', and no default value was defined for '${status}'.`;
                return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
            }
        }

        exp.inError = true;
        exp.errorMessage = `Project context not found to evaluate '${attDef.Name}' attachment '${status}' status.`;
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }

    evaluateIsEmpty(exp) {
        if (!isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            throw Error("project store not registered");
        }

        if (exp.PublicAttachmentDefinitionId !== null) {
            const attachment = this.getters[PROJECT_TYPES.getters.attachmentFromId](exp.PublicAttachmentDefinitionId);
            return { Value: attachment === null };
        } else if (exp.PublicDataFieldDefinitionId !== null) {
            const datafieldValue = this.getters[PROJECT_TYPES.getters.dataFieldValueFromId](
                exp.PublicDataFieldDefinitionId
            );
            return { Value: datafieldValue === null || datafieldValue === "" };
        } else {
            exp.inError = true;
            exp.errorMessage = `There is no Attachment or Data Field defined for the expression`;
            return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
        }
    }

    evaluateEnvelopeStatus(exp, envelopeStatusType) {
        exp.usingDefault = false;
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }

        let status = "";
        switch (envelopeStatusType) {
            case ENVELOPE_STATUS.Completed:
                status = "Is Complete";
                break;
            default:
                throw Error(`Invalid envelope status type (${envelopeStatusType}).`);
        }

        const envelopeDefinition = this.getters[PROGRAM_TYPES.getters.getEnvelopeDefinitionFromId](
            exp.PublicEnvelopeDefinitionId
        );
        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            const envelope = this.getters[PROJECT_TYPES.getters.getEnvelopeFromDefinitionId](
                exp.PublicEnvelopeDefinitionId
            );
            if (envelope) {
                return { Value: isEnvelopeInStatus(envelope, envelopeStatusType) };
            } else if (isFiniteNumber(exp.ConstantValue)) {
                exp.usingDefault = true;
                return { Value: exp.ConstantValue !== BOOLEAN_VALUE.false };
            } else {
                exp.inError = true;
                exp.errorMessage = `There is no envelope '${envelopeDefinition.Name}', and no default value was defined for '${status}'.`;
                return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
            }
        }
        exp.inError = true;
        exp.errorMessage = `Project context not found to evaluate '${envelopeDefinition.Name}' envelope status.`;
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }
}
